/* eslint-disable no-unexpected-multiline */

import styled, { keyframes } from 'styled-components';
import {
  spacer8,
  spacer16,
  gray95,
  white,
  screenSmMax,
  screenMdMin,
} from '@spotify-internal/encore-foundation';

const glueEaseInSoft = 'cubic-bezier(0.3, 0, 0.4, 1)';
const glueEaseOutSoft = 'cubic-bezier(0.6, 0, 0.7, 1)';

const ANIMATION_RUNTIME = 0.3;

const PRE_ANIMATION_DELAY = 0.15;

const POST_ANIMATION_DELAY = 0.35;

const calculateDelay = (
  cardNumber: number,
  delay: number = PRE_ANIMATION_DELAY,
) => cardNumber * delay;

// to make sure shadow stays until all cards come in
// calculate delay based on number of remaining cards
const calculateDuration = (
  cardNumber: number,
  totalCardNum: number,
  waitTime: number = POST_ANIMATION_DELAY,
  delay: number = PRE_ANIMATION_DELAY,
) => {
  const cardIndex = cardNumber - 1;
  const remainingCardsToAnimate = totalCardNum - cardIndex;
  const individualCardDuration = calculateDelay(remainingCardsToAnimate, delay);
  return individualCardDuration + waitTime;
};

const convertToSeconds = (value: number) => `${value.toString()}s`;

const ANIMATION_FILL_MODE = 'both';

const CARD_BOX_SHADOW = `0 ${spacer8} ${spacer16} 0 rgba(0, 0, 0, 0.07)`;

const backgroundColor = keyframes`
  from {
    background-color: ${gray95};
  }

  to {
    background-color: ${white};
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, ${spacer8}, 0);
  }

  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const addShadow = keyframes`
  0% {
    box-shadow: none;
  }

  20% {
    box-shadow: ${CARD_BOX_SHADOW};
  }

  95% {
    animation-timing-function: ${glueEaseOutSoft};
    box-shadow: ${CARD_BOX_SHADOW};
  }

  100% {
    box-shadow: none;
  }
`;

export const CardShadowAnimation = styled.div<{
  cardNum: number;
  totalCardNum: number;
}>`
  animation: ${addShadow}
    ${props =>
      convertToSeconds(calculateDuration(props.cardNum, props.totalCardNum))}
    ${glueEaseInSoft} ${ANIMATION_FILL_MODE};
  animation-delay: ${props => convertToSeconds(calculateDelay(props.cardNum))};
`;

export const CardGrayAnimation = styled.div<{
  cardNum: number;
}>`
  animation: ${backgroundColor} ${convertToSeconds(ANIMATION_RUNTIME)}
    ${glueEaseInSoft} ${ANIMATION_FILL_MODE};
  animation-delay: ${props => convertToSeconds(calculateDelay(props.cardNum))};
`;

export const CardFadeInAnimation = styled.div<{
  cardNum: number;
}>`
  animation: ${fadeIn} ${convertToSeconds(ANIMATION_RUNTIME)} ${glueEaseInSoft}
    ${ANIMATION_FILL_MODE};
  animation-delay: ${props => convertToSeconds(calculateDelay(props.cardNum))};

  @media (max-width: ${screenSmMax}) {
    box-shadow: ${CARD_BOX_SHADOW};
  }

  @media (min-width: ${screenMdMin}) {
    &:hover {
      box-shadow: ${CARD_BOX_SHADOW};
      transition: box-shadow ${convertToSeconds(ANIMATION_RUNTIME)}
        ${glueEaseInSoft};
    }
  }
`;
